import { AxiosRequestConfig } from 'axios';
import {
  ChatMessageListItem,
  ChatMuteType,
  ChatSettingsType,
  type IChatChannelCallSchedule,
  IChatChannelCallSchedulePayload,
  IChatConversation,
  IChatMessage,
  IChatSendMessage,
} from 'src/@types/chat';
import { CustomFile } from 'src/components/upload';
import { Roles } from 'src/features/workspace/WorkspaceShareMemberRoleSelectProps';
import { axios } from 'src/utils/axios';
import { Note } from '../@types/notes-transcription';

export async function getChannels(): Promise<IChatConversation[]> {
  const { data } = await axios.get<IChatConversation[]>('/chat/channel/list');
  return data;
}

export async function getChannelById(
  channelId: string,
): Promise<IChatConversation> {
  const { data } = await axios.get<IChatConversation>(
    `/chat/channel/${channelId}`,
  );
  return data;
}

export async function getChannelMessages(
  channelId: string,
  params?: string,
): Promise<IChatMessage[]> {
  const { data } = await axios.get<IChatMessage[]>(
    `/chat/message/list/${channelId}`,
    {
      params,
    },
  );

  return data;
}

export enum ChatMediaTypes {
  MEDIAS = 'medias',
  FILES = 'files',
  LINKS = 'links',
}

export async function getChannelMedia(
  channelId: string,
  type: ChatMediaTypes,
  nextCursor?: string,
): Promise<IChatMessage[]> {
  const { data } = await axios.get<IChatMessage[]>(
    `/chat/message/list/${type}/${channelId}`,
    {
      params: {
        before: nextCursor,
      },
    },
  );

  return data;
}

export type CreateChannelPayload = {
  type: IChatConversation['type'];
  p2p?: boolean;
  name: IChatConversation['name'];
  participants?: { _id: string }[];
  parentId?: string;
  parentMessageId?: string;
  roles?: IChatConversation['roles'];
  picture?: IChatConversation['picture'];
  settings?: IChatConversation['settings'];
};

export async function createChannel(
  channel: CreateChannelPayload,
): Promise<IChatConversation> {
  const { data } = await axios.post<IChatConversation>(
    `/chat/channel/create`,
    channel,
  );
  return data;
}

export async function deleteChannel(
  channelId: string,
): Promise<IChatConversation> {
  const { data } = await axios.delete<IChatConversation>(
    `/chat/channel/delete/${channelId}`,
  );
  return data;
}

export async function toggleChatConversation(params: {
  channelId: string;
  hide: boolean;
}): Promise<IChatConversation> {
  const { data } = await axios.patch<IChatConversation>(
    `/chat/hide-direct-message`,
    params,
  );
  return data;
}

export type VoiceChannelToken = {
  room: string;
  token: string;
  metadata: {
    egressId: string;
    noteStartedBy: string;
    notesDocument: Note;
  };
};

export async function getVoiceChannelToken(
  channelId: string,
): Promise<VoiceChannelToken> {
  const { data } = await axios.post<VoiceChannelToken>(
    `/chat/channel/voice/token/${channelId}`,
  );
  return data;
}

export async function sendMessage(
  message: IChatSendMessage,
): Promise<IChatMessage> {
  const { data } = await axios.post<IChatMessage>(
    `/chat/message/send`,
    message,
  );
  return data;
}

type DeleteMessageParams = {
  id: string;
  channel: string;
};

export async function deleteMessage(params: DeleteMessageParams): Promise<any> {
  const { data } = await axios.delete<DeleteMessageParams>(
    `/chat/message/delete`,
    { params },
  );
  return data;
}

type EditMessageParams = {
  id: string;
  channel: string;
  content: string;
};

export async function editMessage(
  params: EditMessageParams,
): Promise<ChatMessageListItem> {
  const { data } = await axios.put<ChatMessageListItem>(
    `/chat/message/edit`,
    params,
  );
  return data;
}

type ReactMessageParams = {
  messageId: string;
  channelId: string;
  reaction: string;
};

export async function reactMessage(
  params: ReactMessageParams,
): Promise<ChatMessageListItem> {
  const { data } = await axios.patch<ChatMessageListItem>(
    `/chat/message/reaction`,
    params,
  );
  return data;
}

export type chatParticipants = {
  id: string;
  participants: { _id: string }[];
};

export async function updateParticipantsChannel(
  chatParticipants: chatParticipants,
) {
  const { data } = await axios.put<IChatConversation>(
    `/chat/channel/participant/add`,
    chatParticipants,
  );
  return data;
}

interface paramsToRmvParticipantFromChannel extends AxiosRequestConfig {
  id: string;
  participants: { _id: string }[];
}

export async function removeParticipantsFromChannel(
  chatParticipantsToRemove: paramsToRmvParticipantFromChannel,
) {
  const { data } = await axios.put<IChatConversation>(
    `/chat/channel/participant/remove`,
    chatParticipantsToRemove,
  );
  return data;
}

export async function leaveFromChannel(id: string): Promise<IChatConversation> {
  const { data } = await axios.put<IChatConversation>(
    `/chat/channel/participant/leave`,
    {
      id,
    },
  );
  return data;
}

type infoChannelUpdate = {
  id: string;
  name: string;
  picture: CustomFile | string | null;
  icon?: string;
  settings?: ChatSettingsType;
  description?: string;
};

export async function updateInfoChannel(updateInfo: infoChannelUpdate) {
  const { data } = await axios.put<IChatConversation>(
    `/chat/channel/update`,
    updateInfo,
  );
  return data;
}

type notificationsChannelUpdate = {
  id: string;
  mute?: ChatMuteType;
  muteUntil?: string;
};

export async function updateChannelNotifications(
  updateNotifications: notificationsChannelUpdate,
) {
  const { data } = await axios.patch<IChatConversation>(
    `/chat/channel/settings/mute`,
    updateNotifications,
  );
  return data;
}

export const readMessagesFromChannel = ({
  channelId,
  messages,
}: {
  channelId: string;
  messages: string[];
}) =>
  axios.patch(`/chat/message/read-messages`, {
    messages,
    channelId,
  });

export async function pinMessage(
  message: Pick<IChatSendMessage, 'channel' | 'pinned'> & { id: string },
): Promise<IChatMessage> {
  const { data } = await axios.put<IChatMessage>(`/chat/message/set-pinned`, {
    channelId: message.channel,
    ...message,
  });
  return data;
}

export async function getPinnedMessagesByChannelId(
  channelId: string,
): Promise<ChatMessageListItem[]> {
  const { data } = await axios.get<ChatMessageListItem[]>(
    `/chat/message/list/pinned-messages/${channelId}`,
  );
  return data;
}

export async function getThreadsByChannelId(
  channelId: string,
): Promise<IChatConversation> {
  const { data } = await axios.get<IChatConversation>(
    `/chat/channel/${channelId}/subchannels`,
  );
  return data;
}

export async function toggleThread(
  channelId: string,
  user: string,
): Promise<IChatMessage> {
  const { data } = await axios.patch(`/chat/channel/toggle-follow`, {
    channelId,
    user,
  });
  return data;
}

export type UpdateParticipantRoleProps = {
  id: string;
  participantId: string;
  role: Roles;
};

export async function updateParticipantRole(
  params: UpdateParticipantRoleProps,
): Promise<IChatConversation> {
  const { data } = await axios.put<IChatConversation>(
    `/chat/channel/update/role`,
    params,
  );
  return data;
}

export async function searchChat(
  channelId: string,
  searchTerm: string,
): Promise<ChatMessageListItem[]> {
  const { data } = await axios.get<ChatMessageListItem[]>(
    `/chat/${channelId}/search?q=${searchTerm}`,
  );
  return data;
}

export async function getVoiceChannelCallSchedules(channelId: string) {
  const { data } = await axios.get<IChatChannelCallSchedule[]>(
    `/chat/channel/schedule/${channelId}`,
  );
  return data;
}

export async function createVoiceChannelCallSchedule({
  channelId,
  schedule,
}: { channelId: string; schedule: IChatChannelCallSchedulePayload }) {
  const { data } = await axios.post<IChatChannelCallSchedule>(
    `/chat/channel/schedule/create`,
    {
      channelId,
      ...schedule,
    },
  );
  return data;
}

export async function updateVoiceChannelCallSchedule(
  scheduleId: string,
  schedule: IChatChannelCallSchedulePayload,
) {
  const { data } = await axios.put<IChatChannelCallSchedule>(
    `/chat/channel/schedule/${scheduleId}`,
    schedule,
  );
  return data;
}

export async function removeVoiceChannelCallSchedule(scheduleId: string) {
  const { data } = await axios.delete<IChatChannelCallSchedule>(
    `/chat/channel/schedule/delete?scheduleId=${scheduleId}`,
  );
  return data;
}

export const acceptVoiceInvite = async (channelId: string) => {
  await axios.post('/chat/channel/voice/accept-invite', { channelId });
};

export const declineVoiceInvite = async (channelId: string) => {
  await axios.delete(
    `/chat/channel/voice/decline-invite?channelId=${channelId}`,
  );
};

export * as ChatRequests from './chat';
